// Customer's fair price (spec 20260811). Presentational — QuoteAssessment does
// the fetch (the "Ask" button shares the state to enforce one-ask-per-quote).
//
// `data` is the Prism read: { status, requested_at, answered_at, note, items:
// [{quote_item_id, asking_price_cents}] } or null when never asked. `items` is
// the QA item list (name, sp, bp), so each answer sits beside our own numbers.
//
// The layout exists for ONE decision: how far is their number from what we'd
// pay? Their price is the loud element; ours sit beside it; the badge names the
// gap vs BP so a 40× lowball/highball reads at a glance, not after arithmetic.

import { fmt, gapBadge } from './fairPriceGap.js';

// More-info ask (spec 20260812). Same read endpoint, different shape: the
// customer answers with free text + photos, not per-item prices. `prompt` is what
// the operator asked for, `note` is the customer's answer, `photo_keys` are the
// uploads (viewed in the QA photo gallery — this only counts them).
function MoreInfoResponse({ data }) {
    if (data.status === 'requested') {
        return (
            <div className="qa-fairprice card">
                <div className="qa-lab">Customer&apos;s response</div>
                <div className="qa-muted">Awaiting customer response. The request has been sent.</div>
            </div>
        );
    }

    if (data.status === 'expired') {
        return (
            <div className="qa-fairprice card">
                <div className="qa-lab">Customer&apos;s response</div>
                <div className="qa-fairprice-expired">
                    More info request expired. The customer did not reply. Re-decide with the
                    numbers you have.
                </div>
            </div>
        );
    }

    // answered
    const photoCount = (data.photo_keys || []).length;
    return (
        <div className="qa-fairprice card">
            <div className="qa-lab">Customer&apos;s response</div>
            {data.prompt && (
                <div className="qa-fairprice-note">
                    <span className="qa-fairprice-note-lab">What we asked for</span>
                    {data.prompt}
                </div>
            )}
            {data.note && (
                <div className="qa-fairprice-note">
                    <span className="qa-fairprice-note-lab">Their answer</span>
                    {data.note}
                </div>
            )}
            {photoCount > 0 && (
                <div className="qa-fairprice-note">
                    <span className="qa-fairprice-note-lab">Photos</span>
                    {photoCount} {photoCount === 1 ? 'photo' : 'photos'} attached, in the photo gallery.
                </div>
            )}
            {!data.note && photoCount === 0 && (
                <div className="qa-muted">The customer replied with no note or photos.</div>
            )}
        </div>
    );
}

export default function FairPricePanel({ data, items = [] }) {
    if (!data || !data.status) return null;

    if (data.request_type === 'more_info') return <MoreInfoResponse data={data} />;

    if (data.status === 'requested') {
        return (
            <div className="qa-fairprice card">
                <div className="qa-lab">Customer&apos;s fair price</div>
                <div className="qa-muted">Awaiting customer price — the ask has been sent.</div>
            </div>
        );
    }

    if (data.status === 'expired') {
        return (
            <div className="qa-fairprice card">
                <div className="qa-lab">Customer&apos;s fair price</div>
                <div className="qa-fairprice-expired">
                    Extra info request expired — the customer did not reply. Re-decide with
                    the numbers you have.
                </div>
            </div>
        );
    }

    // answered
    const askByItem = new Map(
        (data.items || []).map((a) => [a.quote_item_id, a.asking_price_cents]),
    );
    return (
        <div className="qa-fairprice card">
            <div className="qa-lab">Customer&apos;s fair price</div>
            {items.map((it, idx) => {
                const cents = it.quote_item_id ? askByItem.get(it.quote_item_id) : undefined;
                const theirs = cents == null ? null : cents / 100;
                const badge = theirs == null ? null : gapBadge(theirs, it.bp);
                return (
                    <div className="qa-fairprice-item" key={it.quote_item_id ?? idx}>
                        <div className="qa-fairprice-name">{it.name}</div>
                        <div className="qa-fairprice-nums">
                            <div className="qa-fairprice-theirs">
                                <span className="qa-fairprice-theirs-lab">Their price</span>
                                <span className="qa-fairprice-theirs-val">
                                    {theirs == null ? '—' : `£${fmt(theirs)}`}
                                </span>
                            </div>
                            <div className="qa-fairprice-ours">
                                SP £{fmt(it.sp) ?? '—'} &middot; BP £{fmt(it.bp) ?? '—'}
                            </div>
                            {badge && (
                                <span className={`qa-fairprice-gap qa-fairprice-gap--${badge.tone}`}>
                                    {badge.label}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
            {data.note && (
                <div className="qa-fairprice-note">
                    <span className="qa-fairprice-note-lab">Customer note</span>
                    {data.note}
                </div>
            )}
        </div>
    );
}
